import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes } from 'react-icons/fa';

const HeaderShell = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--color-cream);
  border-bottom: 1px solid var(--border-color);
`;
const Row = styled.div`
  max-width: 1160px;
  margin-inline: auto;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0;
  width: calc(100% - 3rem);
  @media (max-width: 480px) { width: calc(100% - 2rem); min-height: 72px; gap: 0.5rem; }
`;
const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--color-midnight);
  font-size: 1.8rem;
  letter-spacing: -0.06em;
  font-weight: 800;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: var(--color-midnight); }
  img { width: 58px; height: 58px; object-fit: contain; }
  @media (max-width: 480px) { font-size: 1.65rem; img { width: 50px; height: 50px; } }
  @media (max-width: 380px) { font-size: 1.45rem; img { width: 42px; height: 42px; } }
`;
const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.75rem;
  @media (max-width: 700px) { display: none; }
`;
const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  font-size: 0.875rem;
  font-weight: 650;
  color: var(--color-text-light);
  &[aria-current='page'], &:hover { color: var(--color-midnight); text-decoration: underline; text-underline-offset: 6px; }
`;
const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.65rem 1.1rem;
  border-radius: 8px;
  color: var(--color-cream);
  background: var(--color-midnight);
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
  &:hover { color: var(--color-cream); background: #352768; }
`;
const MobileActions = styled.div`
  display: none;
  align-items: center;
  gap: 0.5rem;
  @media (max-width: 700px) { display: flex; }
  @media (max-width: 380px) { gap: 0.35rem; ${DownloadLink} { padding-inline: 0.7rem; font-size: 0.8rem; } }
`;
const Toggle = styled.button`
  width: 44px;
  height: 44px;
  padding: 0;
  display: grid;
  place-items: center;
  color: var(--color-midnight);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1.05rem;
`;
const MobileNav = styled.nav`
  padding: 0.5rem 1rem 1rem;
  border-top: 1px solid var(--border-color);
  ${NavLink} { display: flex; padding: 0.5rem; }
  &[hidden] { display: none; }
  @media (min-width: 701px) { display: none; }
`;
const Header: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        document.getElementById('navigation-toggle')?.focus();
      }
    };
    if (open) document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [open]);
  const links = <>
    <NavLink to="/rules" aria-current={pathname === '/rules' ? 'page' : undefined}>How to play</NavLink>
    <NavLink to="/blog/" aria-current={pathname.startsWith('/blog') ? 'page' : undefined}>The notebook</NavLink>
  </>;
  return <HeaderShell>
    <Row>
      <Logo to="/" aria-label="Pasur home"><img src="/images/pasur-mark.png" width="58" height="58" alt="" />Pasur</Logo>
      <DesktopNav aria-label="Main navigation">{links}<DownloadLink href="/#download">Get the app</DownloadLink></DesktopNav>
      <MobileActions>
        <DownloadLink href="/#download">Get the app</DownloadLink>
        <Toggle id="navigation-toggle" aria-label={open ? 'Close navigation' : 'Open navigation'} aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen(!open)}>{open ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}</Toggle>
      </MobileActions>
    </Row>
    <MobileNav id="mobile-navigation" aria-label="Mobile navigation" hidden={!open}>{links}<NavLink to="/" onClick={() => setOpen(false)}>Home</NavLink></MobileNav>
  </HeaderShell>;
};
export default Header;
